/**
 * WEB-23: панель «Защита переписки» — всё про безопасность в одном месте,
 * вместо трёх пересказов в самой переписке.
 *
 * 🔴 Зачем. Справку «сервер видит только запечатанные конверты» человек уже
 * прочитал на лендинге приглашения и в памятке, а в кабинете владельца она
 * занимала ЦЕЛУЮ строку над лентой на обеих страницах чата. Слово владельца
 * 02.09: «сейчас это по сути ни к чему и удобства не прибавляет, наоборот».
 * Полоса «Устройство собеседника ещё не проверено» при этом висит у всех и
 * ВСЕГДА: снимается она только кнопкой «Код сверен», а её в пилоте не нажимал
 * никто. Значит это не редкое состояние, а постоянный налог на экран.
 *
 * Что осталось в переписке и почему:
 *  - замок в шапке — признак защиты обязан быть виден всегда
 *    (решение записано в `joinLanding.jsx:428`, «замок — не украшение»);
 *  - строка «ключ СМЕНИЛСЯ» — это не справка, а событие с ценой: на нём
 *    стоит запрет отправки (WEB-14). Спрятать его — отдать канал тому, кто
 *    ключ подменил;
 *  - «чат не сохранится на этом устройстве» — предупреждение о потере всей
 *    истории, а не рассказ об устройстве шифрования.
 *
 * 🔴 Компонент намеренно ЧИСТЫЙ: без сети, без роутера, без axios кабинета.
 * Он живёт и в кабинете владельца, и на публичной странице `/j/chat` — как
 * `pinnedWarnings.jsx`, и по той же причине: иначе он потянул бы в публичную
 * страницу весь каркас кабинета.
 */
import React, { useEffect, useRef } from 'react';
// Тот же формат даты, что и в вопросе «перенести сюда?» — иначе одно и то же
// устройство было бы «подключено 25.08.2026» там и «2026-08-25T…» здесь.
import { connectedOn } from './deviceTakeover';

/**
 * Сколько устройств просят внимания. Считается ТЕМ ЖЕ правилом, что и
 * закреплённая строка (`pinnedWarnings`): непроверенное — это «первый
 * контакт» и «ключ сменился». Иначе цифра у замка и текст в панели разошлись
 * бы во мнениях — ровно та ошибка, которую ревью нашло у строки доверия 30.08.
 */
export const unverifiedCount = (people = []) => people.filter(
  (p) => p?.status === 'first_use' || p?.status === 'key_changed',
).length;

/**
 * Замок в шапке. Он же — вход в панель.
 *
 * Слово владельца 03.09: рядом с замком стоит ЦИФРА непроверенных, а не
 * молчаливая смена цвета. Цвет в шапке легко не заметить, на солнце и в
 * тёмной теме — тем более, и он ничего не говорит о количестве.
 */
export const PANEL_ID = 'msgSecurityPanel';

export const SecurityLock = ({
  count = 0, onOpen, expanded = false, lockRef = null, testId = 'security-lock',
}) => (
  <button
    type="button"
    className="msgSecurityLock"
    ref={lockRef}
    data-testid={testId}
    data-count={count}
    aria-expanded={expanded}
    // 🔴 `aria-controls` обязателен: в кабинете панель лежит в ДРУГОЙ ветке DOM
    // (шапка каркаса → `.cabinetContent`), и без связи читалка не найдёт, чем
    // именно управляет эта кнопка (ревью 03.09).
    aria-controls={PANEL_ID}
    aria-label={count > 0
      ? `Защита переписки: непроверенных устройств ${count}`
      : 'Защита переписки'}
    onClick={onOpen}
  >
    <span aria-hidden="true">🔒</span>
    {count > 0 && (
      <span className="msgSecurityLockCount" data-testid={`${testId}-count`}>{count}</span>
    )}
  </button>
);

const STATUS_TEXT = {
  key_changed: 'Ключ устройства СМЕНИЛСЯ — сверьте код заново',
  first_use: 'Ещё не проверено',
  verified: 'Код сверен',
  unchanged: 'Устройство знакомое, код не сверялся',
};

/**
 * @param {Array}  people        [{key, name, status, ed25519?}] — кто в разговоре
 * @param {string} safetyNumber  код безопасности (в 1:1 он один на переписку)
 * @param {Object} myDevice      {did, created_at} — своё устройство (Д-20 §3)
 * @param {Function} onVerify    (key) => void — «Код сверен»
 * @param {Function} onClose     закрыть панель
 */
const SecurityPanel = ({
  people = [],
  safetyNumber = null,
  // 🔴 Кого именно сверять — слово страницы, а не «собеседника» вообще: в
  // кабинете это «участника» или «сотрудника», и различие было пином ещё до
  // панели. Потерять его при переезде значило бы тихо обеднить текст.
  peerWord = 'собеседника',
  myDevice = null,
  onVerify,
  onClose,
}) => {
  const closeRef = useRef(null);

  // Фокус приходит в панель — иначе для незрячего нажатие на замок не меняет
  // ничего (та же причина, по которой у вопроса о выходе фокус уводится на
  // «Отмена», WEB-22).
  useEffect(() => { closeRef.current?.focus(); }, []);

  return (
    <section
      className="msgSecurityPanel"
      id={PANEL_ID}
      data-testid="security-panel"
      role="dialog"
      aria-labelledby="msgSecurityPanelTitle"
      // Не `aria-modal`: блок инлайновый и ничего не перекрывает — тот же
      // разбор, что у вопроса перед выходом из чата.
      onKeyDown={(e) => { if (e.key === 'Escape') onClose?.(); }}
    >
      <div className="msgSecurityPanelHead">
        <h3 id="msgSecurityPanelTitle">Защита переписки</h3>
        <button type="button" ref={closeRef} onClick={onClose} data-testid="security-close">
          Закрыть
        </button>
      </div>

      <p className="msgSecurityPanelWhat">
        Сообщения запечатываются на вашем устройстве и распечатываются на
        устройстве собеседника. Сервер видит только запечатанные конверты и
        открыть их не может.
      </p>

      {people.length > 0 && (
        <>
          <h4>Кто в разговоре</h4>
          <ul className="msgSecurityPeople">
            {people.map((p) => (
              <li key={p.key} data-testid={`security-person-${p.key}`} data-status={p.status}>
                <span className="msgSecurityPersonName">{p.name}</span>
                <span className="msgSecurityPersonState">
                  {STATUS_TEXT[p.status] ?? 'Состояние неизвестно'}
                </span>
                {(p.status === 'first_use' || p.status === 'key_changed') && (
                  <button
                    type="button"
                    data-testid={`security-verify-${p.key}`}
                    onClick={() => onVerify?.(p.key)}
                  >
                    Код сверен
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {safetyNumber && (
        <>
          <h4>Код безопасности</h4>
          <p className="msgSecurityPanelHint">
            {`Число должно совпасть с экраном ${peerWord} до цифры:`}
          </p>
          <code className="msgSecurityNumber" data-testid="security-number">{safetyNumber}</code>
        </>
      )}

      {/* 🔴 `is_mine` и `active` — не украшение (ревью 03.09, High). Ручка
          `GET /msg/devices/mine` отдаёт АКТИВНОЕ устройство принципала, а не
          обязательно наше: в состоянии Д-20 («чат подключён к телефону»)
          панель доступна из шапки, и без этой проверки человек читал бы
          «Подключено 25.08. Ключи хранятся только здесь» про ЧУЖОЕ
          устройство — ровно когда решает, переносить ли чат. */}
      {myDevice?.active && myDevice?.is_mine && myDevice?.did && (
        <>
          <h4>Это устройство</h4>
          <p className="msgSecurityPanelHint" data-testid="security-my-device">
            {connectedOn(myDevice.created_at)
              ? `Подключено ${connectedOn(myDevice.created_at)}. `
              : ''}
            Ключи от переписки хранятся только здесь.
          </p>
        </>
      )}
    </section>
  );
};

export default SecurityPanel;
